import { useState } from "react";
import { AspectRatio, Box, Flex, Skeleton, Spinner, Text, VStack } from "@chakra-ui/react";
import { Clock, VideoCamera } from "@phosphor-icons/react";
import { asHTML } from "@prismicio/client";
import { richTextSx } from "./richTextStyles";

const GREEN = "#5A8A1A";
const HEADING_INK = "#0E3137";

// Converts a Loom share URL (https://www.loom.com/share/{id}) to its embeddable
// iframe URL (https://www.loom.com/embed/{id}) so the video plays inline on the
// page. Returns null for anything else (video field is a plain Web link, so it's
// not guaranteed to always be Loom).
function toLoomEmbedUrl(url) {
  const match = url?.match(/^https?:\/\/(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)/);
  return match ? `https://www.loom.com/embed/${match[1]}` : null;
}

// Video walkthrough, embedded and playable directly on the page — heading, "what
// you'll learn" line, and duration sit above the player; sub_heading (if set)
// sits below the heading. Falls back to a plain "watch the video" external link
// if the URL isn't an embeddable Loom link.
export default function VideoCallOutSlice({ slice }) {
  const { heading, sub_heading, what_you_will_learn, video, duration } = slice.primary;
  const embedUrl = video?.link_type === "Web" ? toLoomEmbedUrl(video.url) : null;
  const externalUrl = !embedUrl && video?.link_type === "Web" ? video.url : null;
  // Loom's embed iframe takes a moment to load on entry — show a shimmering
  // placeholder with a spinner until it fires onLoad, then reveal the player.
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  return (
    <VStack align="stretch" gap={4}>
      <VStack align="start" gap={1}>
        {heading && (
          <Flex align="center" gap={2}>
            <VideoCamera size={20} color={GREEN} weight="fill" />
            <Text fontWeight="700" fontSize="body.l" color={HEADING_INK}>
              {heading}
            </Text>
          </Flex>
        )}
        {sub_heading?.length > 0 && <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: asHTML(sub_heading) }} />}
        <Flex align="center" gap={4} flexWrap="wrap">
          {what_you_will_learn && (
            <Text fontSize="body.s" color="gray.600">
              {what_you_will_learn}
            </Text>
          )}
          {duration && (
            <Flex align="center" gap={1} color={GREEN}>
              <Clock size={14} />
              <Text fontSize="body.xs" fontWeight="600">
                {duration}
              </Text>
            </Flex>
          )}
        </Flex>
      </VStack>

      {embedUrl && (
        <Box position="relative" w="100%">
          <AspectRatio ratio={16 / 9} w="100%" visibility={isVideoLoaded ? "visible" : "hidden"}>
            <Box
              as="iframe"
              src={embedUrl}
              allow="fullscreen"
              allowFullScreen
              title={heading || "Video"}
              borderRadius="lg"
              onLoad={() => setIsVideoLoaded(true)}
            />
          </AspectRatio>
          {!isVideoLoaded && (
            <AspectRatio ratio={16 / 9} w="100%" position="absolute" top={0} left={0}>
              <Box position="relative">
                {/* Skeleton's baseStyle forces all children to visibility:hidden, so
                    the spinner is a sibling positioned on top, not nested inside it. */}
                <Skeleton position="absolute" inset={0} borderRadius="lg" startColor="gray.100" endColor="gray.200" />
                <Flex position="absolute" inset={0} align="center" justify="center">
                  <Spinner color={GREEN} thickness="3px" size="lg" />
                </Flex>
              </Box>
            </AspectRatio>
          )}
        </Box>
      )}

      {externalUrl && (
        <Flex
          as="a"
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          align="center"
          gap={2}
          bg="lightLime"
          border="1px solid"
          borderColor="lime"
          borderRadius="lg"
          p={4}
          w="fit-content"
          cursor="pointer"
          _hover={{ opacity: 0.9 }}
          transition="opacity 0.15s"
        >
          <Text fontWeight="600" color={GREEN}>
            Watch the video
          </Text>
        </Flex>
      )}
    </VStack>
  );
}
